import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import updatePlatformConfigs from 'redux/actions/Common/updatePlatformConfigs';
import RiskBandsEditor from 'components/RiskBands/RiskBandsEditor';
import {
  toRiskBandPayload,
  toRiskBandRows,
  validateRiskBands,
} from 'utils/riskBands';

/**
 * Platform-wide default risk score bands: what an org's risky deals are
 * classified by when the org hasn't set bands of its own.
 *
 * The `GET` always answers with a non-empty array -- the hardcoded fallback is
 * decorated in at read time -- so there is no "unset" state to render here,
 * only "these are the defaults in force".
 */
const RiskBandsConfig = ({ platformConfigs, onUpdated }) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState(() =>
    toRiskBandRows(platformConfigs?.riskBands),
  );
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (platformConfigs) setRows(toRiskBandRows(platformConfigs?.riskBands));
  }, [platformConfigs]);

  const error = validateRiskBands(rows);

  const handleUpdate = () => {
    if (error) return;
    setUpdating(true);
    dispatch(
      updatePlatformConfigs({ riskBands: toRiskBandPayload(rows) }, () => {
        setUpdating(false);
        alert('Information Updated');
        if (typeof onUpdated === 'function') onUpdated();
      }),
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        border: '1px solid #d3d3d3',
        borderRadius: 2,
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          gap: 2,
        }}
      >
        <Box>
          <Typography>Deal Risk Classification</Typography>
          <Typography variant="caption" color="text.secondary">
            Default score ranges every deal&apos;s weekly risk score is
            classified into. An organization that sets its own bands (under
            Organizations) overrides these; every other organization is
            classified by them.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled={updating || !!error}
          onClick={handleUpdate}
        >
          {updating ? 'Updating...' : 'Update'}
        </Button>
      </Box>
      <Divider orientation="horizontal" flexItem />
      <RiskBandsEditor rows={rows} onChange={setRows} disabled={updating} />
    </Box>
  );
};

RiskBandsConfig.propTypes = {
  platformConfigs: PropTypes.object,
  onUpdated: PropTypes.func,
};

export default RiskBandsConfig;
